import React, { useEffect, useState } from 'react';
import {
  Store, MapPin, Map, Building2, Thermometer, Box,
  Users, Shield, ClipboardCheck, Building, ChartBar
} from 'lucide-react';
import api from './api';

const StatCard = ({ title, count, percentage, icon: Icon, bgColor, subtitle }) => (
  <div className={`${bgColor} rounded-xl shadow-sm p-6 text-white h-32`}>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-medium opacity-90 text-sm">{title}</h3>
        <p className="text-2xl font-bold mt-1">
          {count?.toLocaleString()}
          {percentage !== undefined && ` (${percentage}%)`}
        </p>
        {subtitle && (
          <p className="text-xs mt-1 opacity-80">{subtitle}</p>
        )}
      </div>
      <div className="bg-white/20 p-2 rounded-xl">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const SectionTitle = ({ title, icon: Icon, gradientClass }) => (
  <div className="mb-4 flex items-center">
    <div className={`${gradientClass} p-2 rounded-lg shadow-sm`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <h2 className="text-xl font-bold ml-3 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
      {title}
    </h2>
  </div>
);

const DataTable = ({ data, title, total, headerColor = "bg-gradient-to-br from-violet-500 to-violet-700" }) => {
  const realTotal = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm h-[400px] flex flex-col overflow-hidden">
      <div className={`${headerColor} p-4`}>
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-white text-lg">Dépôts par {title}</h3>
          <span className="text-sm bg-white/20 backdrop-blur-sm rounded-lg px-4 py-1.5 text-white font-medium">
            Total: {realTotal.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50/90 backdrop-blur-sm border-b">
                  Nom
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50/90 backdrop-blur-sm border-b">
                  Nombre
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50/90 backdrop-blur-sm border-b">
                  %
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data
                .sort((a, b) => b.count - a.count)
                .map((item, index) => {
                  const percentage = (item.count / realTotal) * 100;
                  const isHighValue = percentage > 10;

                  return (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.region || item.departement || item.commune}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                        {item.count.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-semibold ${isHighValue ? 'text-indigo-600' : 'text-gray-500'
                        }`}>
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatSection = ({ title, icon, gradientClass, children, className = "" }) => (
  <div className={`mb-4 ${className}`}>
    <SectionTitle title={title} icon={icon} gradientClass={gradientClass} />
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {children}
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/collect/stats/');
        setStats(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, sectionIndex) => (
              <div key={sectionIndex}>
                <div className="h-8 bg-gray-200 rounded w-48 mb-4" />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-gray-200 rounded-xl h-32" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <StatSection
          title="Statistiques Générales"
          icon={ChartBar}
          gradientClass="bg-gradient-to-br from-violet-500 to-violet-700"
        >
          <StatCard
            title="Total Dépôts"
            count={stats.total_stats.total_depots}
            icon={Store}
            bgColor="bg-gradient-to-br from-violet-500 to-violet-700"
          />
          <StatCard
            title="Régions"
            count={stats.regions.detail.length}
            icon={Map}
            bgColor="bg-gradient-to-br from-emerald-500 to-emerald-700"
            subtitle={`${stats.regions.total} dépôts répartis`}
          />
          <StatCard
            title="Départements"
            count={stats.departements.total}
            icon={MapPin}
            bgColor="bg-gradient-to-br from-blue-500 to-blue-700"
            subtitle={`${stats.departements.detail.length} départements`}
          />
          <StatCard
            title="Communes"
            count={stats.communes.total}
            icon={Building2}
            bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
            subtitle={`${stats.communes.detail.length} communes`}
          />
        </StatSection>

        <StatSection
          title="Propriétaires et Conformité"
          icon={Users}
          gradientClass="bg-gradient-to-br from-pink-500 to-pink-700"
        >
          <StatCard
            title="Propriétaires Homme"
            count={stats.proprietaires.genre.homme.count}
            percentage={stats.proprietaires.genre.homme.percentage}
            icon={Users}
            bgColor="bg-gradient-to-br from-sky-500 to-sky-700"
          />
          <StatCard
            title="Propriétaires Femme"
            count={stats.proprietaires.genre.femme.count}
            percentage={stats.proprietaires.genre.femme.percentage}
            icon={Users}
            bgColor="bg-gradient-to-br from-pink-500 to-pink-700"
          />
          <StatCard
            title="Connaissance Arrêté"
            count={stats.conformite.connaissance_arrete.count}
            percentage={stats.conformite.connaissance_arrete.percentage}
            icon={ClipboardCheck}
            bgColor="bg-gradient-to-br from-yellow-500 to-yellow-700"
          />
          <StatCard
            title="Avec Supervision"
            count={stats.conformite.avec_supervision.count}
            percentage={stats.conformite.avec_supervision.percentage}
            icon={Shield}
            bgColor="bg-gradient-to-br from-green-500 to-green-700"
          />
        </StatSection>

        <StatSection
          title="Infrastructure"
          icon={Building}
          gradientClass="bg-gradient-to-br from-cyan-500 to-cyan-700"
          className="lg:grid-cols-2"
        >
          <StatCard
            title="Climatisation"
            count={stats.infrastructure.climatises.count}
            percentage={stats.infrastructure.climatises.percentage}
            icon={Thermometer}
            bgColor="bg-gradient-to-br from-cyan-500 to-cyan-700"
          />
          <StatCard
            title="Réfrigérateur"
            count={stats.infrastructure.avec_refrigerateur.count}
            percentage={stats.infrastructure.avec_refrigerateur.percentage}
            icon={Box}
            bgColor="bg-gradient-to-br from-teal-500 to-teal-700"
          />
        </StatSection>

        <div className="grid gap-4 lg:grid-cols-3">
          <DataTable
            data={stats.regions.detail}
            title="région"
            total={stats.regions.total}
            headerColor="bg-gradient-to-br from-violet-500 to-violet-700"
          />
          <DataTable
            data={stats.departements.detail}
            title="département"
            total={stats.departements.total}
            headerColor="bg-gradient-to-br from-emerald-500 to-emerald-700"
          />
          <DataTable
            data={stats.communes.detail}
            title="commune"
            total={stats.communes.total}
            headerColor="bg-gradient-to-br from-blue-500 to-blue-700"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;